import { Headphones, BookOpen, PenLine, Mic, LayoutGrid } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

/** Practice browse sections (URL `?section=`). */
export const PRACTICE_SECTIONS = ['mocks', 'listening', 'reading', 'writing', 'speaking'] as const;

export type PracticeSection = (typeof PRACTICE_SECTIONS)[number];

export function isPracticeSection(value: unknown): value is PracticeSection {
  return typeof value === 'string' && (PRACTICE_SECTIONS as readonly string[]).includes(value);
}

export const PRACTICE_SECTION_META: Record<
  PracticeSection,
  { label: string; description: string; icon: LucideIcon }
> = {
  mocks: {
    label: 'Full mocks',
    description: 'Full timed tests under exam conditions',
    icon: LayoutGrid,
  },
  listening: {
    label: 'Listening',
    description: 'Audio sections and parts',
    icon: Headphones,
  },
  reading: {
    label: 'Reading',
    description: 'Passages and question types',
    icon: BookOpen,
  },
  writing: {
    label: 'Writing',
    description: 'Tasks with word limits and timers',
    icon: PenLine,
  },
  speaking: {
    label: 'Speaking',
    description: 'Recorded responses and cue cards',
    icon: Mic,
  },
};

export function skillMatchesSection(
  skill: string | null | undefined,
  section: Exclude<PracticeSection, 'mocks'>,
): boolean {
  if (!skill) return false;
  if (skill === section) return true;
  // e.g. PTE "speaking-writing" appears under both skill tabs
  return skill.split(/[-_/]/).includes(section);
}

/** Student catalog hides e2e-only blueprints. */
export function isStudentVisibleBlueprint(slug: string): boolean {
  return !slug.startsWith('e2e-');
}

export function formatBlueprintTitle(slug: string): string {
  return slug.replace(/-/g, ' ');
}

export function formatDuration(seconds: number): string {
  if (seconds <= 0) return '—';
  if (seconds % 3600 === 0) {
    const hours = seconds / 3600;
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  if (seconds >= 3600) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.round((seconds % 3600) / 60);
    if (minutes === 0) return hours === 1 ? '1 hour' : `${hours} hours`;
    return `${hours} h ${minutes} min`;
  }
  const minutes = Math.round(seconds / 60);
  return minutes === 1 ? '1 minute' : `${minutes} minutes`;
}
